import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isAdmin } from "../../../utils/permissions";
import { useAuth } from "../../../utils/useAuth";
import { useSelector } from "react-redux";
import AppModal from "../AppModal/AppModal";
import "./AddUserModal.scss";
import { BASE_API_URL } from "../../../utils/consts";
import axios from "axios";

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  onAddUser: (data: any) => void;
  userData: any;
};

const COLORS = [
  "oklch(0.6 0.15 25)",
  "oklch(0.7 0.14 70)",
  "oklch(0.62 0.14 150)",
  "oklch(0.55 0.16 265)",
  "oklch(0.55 0.16 320)",
  "oklch(0.5 0.02 260)",
  "oklch(0.65 0.15 195)",
  "oklch(0.48 0.2 280)",
  "oklch(0.68 0.12 95)",
  "oklch(0.58 0.18 10)",
  "oklch(0.62 0.13 230)",
];

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  gender: "",
  password: "",
  role: "",
  locationId: "",
  assignedLocationId: "",
  workingPatternId: "",
  positionInSector: "",
  contractStart: "",
  contractEnd: "",
  sanitaryBookletIssuedAt: "",
  sanitaryBookletValidUntil: "",
  color: COLORS[3],
};

type Errors = Partial<Record<keyof typeof emptyForm, string>>;

const PersonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c0-3.6 3.1-5.6 7-5.6s7 2 7 5.6" />
  </svg>
);

const ChevDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const EyeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 7 10 7a18 18 0 0 1-3 3.7M6.1 6.1A18 18 0 0 0 2 11s3.5 7 10 7a10 10 0 0 0 4-.8M3 3l18 18" />
  </svg>
);

const AddUserModal: React.FC<Props> = ({ isOpen, onCloseModal, onAddUser, userData }) => {
  const { t } = useTranslation();
  const authUser: any = useAuth();
  const [locations, setLocations] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState<Errors>({});
  const [showPassword, setShowPassword] = useState(false);

  const workingPatterns = useSelector((state: any) => state.workingPattern.contracts);
  const isAdminUser = isAdmin(authUser?.roles);

  useEffect(() => {
    if (userData?.clientId && isOpen) {
      axios
        .get(`${BASE_API_URL}/admin/${userData?.clientId}/getPositions`)
        .then((res) => setPositions(res.data))
        .catch(() => {});
      axios
        .get(`${BASE_API_URL}/${userData?.clientId}/locations`)
        .then((res) => setLocations(res.data))
        .catch(() => {});
    }
    if (!isOpen) {
      setFormData(emptyForm);
      setErrors({});
      setShowPassword(false);
    }
  }, [isOpen, userData?.clientId]);

  const setField = (key: keyof typeof emptyForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [key]: value,
      // reset position when location changes
      ...(key === "locationId" ? { positionInSector: "" } : {}),
    }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const filteredPositions = positions.filter(
    (pos: any) => pos?.workSector?.location?._id?.toString() === formData.locationId
  );

  const validate = (): Errors => {
    const e: Errors = {};
    if (!formData.firstName.trim()) e.firstName = "Ime je obavezno";
    if (!formData.lastName.trim()) e.lastName = "Prezime je obavezno";
    if (!formData.email.trim()) e.email = "Email je obavezan";
    if (!formData.password.trim()) e.password = "Šifra je obavezna";
    if (!formData.gender) e.gender = "Pol je obavezan";
    if (!formData.role) e.role = "Uloga je obavezna";
    if (!formData.contractStart) e.contractStart = "Datum početka je obavezan";
    if (isAdminUser) {
      if (!formData.locationId) e.locationId = "Lokacija je obavezna";
      if (!formData.workingPatternId) e.workingPatternId = "Tip ugovora je obavezan";
    }
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    onAddUser({
      ...formData,
      positionInSector: formData.positionInSector ? [formData.positionInSector] : [],
    });
  };

  const selectClass = (val: string, err?: string) =>
    `am-select${!val ? " am-placeholder" : ""}${err ? " am-input-error" : ""}`;

  return (
    <AppModal
      open={isOpen}
      onClose={onCloseModal}
      icon={<PersonIcon />}
      eyebrow="Novi zaposleni"
      title="Dodaj zaposlenog"
      description="Popunite podatke zaposlenog, pristup i ugovor."
      onSubmit={handleSubmit}
      submitLabel="Sačuvaj zaposlenog"
      maxWidth={680}
    >
      {/* ── Lični podaci ── */}
      <div className="am-section">
        <div className="am-section-label">Lični podaci</div>
        <div className="am-form-grid">

          <div className="am-field">
            <label className="am-label">Ime <span className="am-req">*</span></label>
            <input
              className={`am-input${errors.firstName ? " am-input-error" : ""}`}
              placeholder="npr. Marko"
              value={formData.firstName}
              onChange={setField("firstName")}
            />
            {errors.firstName && <span className="am-error-msg">{errors.firstName}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">Prezime <span className="am-req">*</span></label>
            <input
              className={`am-input${errors.lastName ? " am-input-error" : ""}`}
              placeholder="npr. Ilić"
              value={formData.lastName}
              onChange={setField("lastName")}
            />
            {errors.lastName && <span className="am-error-msg">{errors.lastName}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">Email <span className="am-req">*</span></label>
            <input
              className={`am-input${errors.email ? " am-input-error" : ""}`}
              type="email"
              placeholder="ime@firma.com"
              value={formData.email}
              onChange={setField("email")}
            />
            {errors.email && <span className="am-error-msg">{errors.email}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">Pol <span className="am-req">*</span></label>
            <div className="am-select-wrap">
              <select
                className={selectClass(formData.gender, errors.gender)}
                value={formData.gender}
                onChange={setField("gender")}
              >
                <option value="">Molimo izaberite</option>
                <option value="male">Muški</option>
                <option value="female">Ženski</option>
                <option value="other">Ne želim da navedem</option>
              </select>
              <span className="am-chev"><ChevDown /></span>
            </div>
            {errors.gender && <span className="am-error-msg">{errors.gender}</span>}
          </div>
        </div>
      </div>

      {/* ── Nalog i pristup ── */}
      <div className="am-section">
        <div className="am-section-label">Nalog i pristup</div>
        <div className="am-form-grid">

          <div className="am-field am-span-2">
            <label className="am-label">Šifra <span className="am-req">*</span></label>
            <div className="am-input-affix">
              <input
                className={`am-input am-input-trail${errors.password ? " am-input-error" : ""}`}
                type={showPassword ? "text" : "password"}
                placeholder="Min. 8 karaktera"
                value={formData.password}
                onChange={setField("password")}
              />
              <button
                type="button"
                className="am-ic-trail-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Sakrij šifru" : "Prikaži šifru"}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {errors.password && <span className="am-error-msg">{errors.password}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">Uloga <span className="am-req">*</span></label>
            <div className="am-select-wrap">
              <select
                className={selectClass(formData.role, errors.role)}
                value={formData.role}
                onChange={setField("role")}
              >
                <option value="">Izaberite ulogu</option>
                <option value="user">Zaposleni</option>
                <option value="moderator">Menadžer</option>
                <option value="admin">Administrator</option>
              </select>
              <span className="am-chev"><ChevDown /></span>
            </div>
            {errors.role && <span className="am-error-msg">{errors.role}</span>}
          </div>

          {isAdminUser && (
            <div className="am-field">
              <label className="am-label">Lokacija <span className="am-req">*</span></label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.locationId, errors.locationId)}
                  value={formData.locationId}
                  onChange={setField("locationId")}
                >
                  <option value="">Izaberi lokaciju</option>
                  {locations.filter((loc: any) => !loc.frozen).map((loc: any) => (
                    <option key={loc._id} value={loc._id}>{loc.name}</option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
              {errors.locationId && <span className="am-error-msg">{errors.locationId}</span>}
            </div>
          )}

          {isAdminUser && (
            <div className="am-field">
              <label className="am-label">
                Ograničenje lokacije
                <span className="am-field-hint">opciono</span>
              </label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.assignedLocationId)}
                  value={formData.assignedLocationId}
                  onChange={setField("assignedLocationId")}
                >
                  <option value="">Bez ograničenja (sve lokacije)</option>
                  {locations.filter((loc: any) => !loc.frozen).map((loc: any) => (
                    <option key={loc._id} value={loc._id}>{loc.name}</option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Pozicija i ugovor ── */}
      <div className="am-section">
        <div className="am-section-label">Pozicija i ugovor</div>
        <div className="am-form-grid">

          {isAdminUser && (
            <div className="am-field">
              <label className="am-label">Tip ugovora <span className="am-req">*</span></label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.workingPatternId, errors.workingPatternId)}
                  value={formData.workingPatternId}
                  onChange={setField("workingPatternId")}
                >
                  <option value="">Izaberite tip ugovora</option>
                  {workingPatterns?.map((wp: any) => (
                    <option key={wp._id} value={wp._id}>{wp.contractName}</option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
              {errors.workingPatternId && <span className="am-error-msg">{errors.workingPatternId}</span>}
            </div>
          )}

          {isAdminUser && (
            <div className="am-field">
              <label className="am-label">
                Pozicija u sektoru
                <span className="am-field-hint">opciono</span>
              </label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.positionInSector)}
                  value={formData.positionInSector}
                  onChange={setField("positionInSector")}
                >
                  <option value="">
                    {!formData.locationId ? "Prvo izaberite lokaciju…" : "Izaberite poziciju"}
                  </option>
                  {filteredPositions.map((pos: any) => (
                    <option key={pos._id} value={pos._id}>
                      {pos.name} ({pos.workSector?.name})
                    </option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>
          )}

          <div className="am-field">
            <label className="am-label">Početak rada <span className="am-req">*</span></label>
            <input
              className={`am-input am-date${errors.contractStart ? " am-input-error" : ""}`}
              type="date"
              value={formData.contractStart}
              onChange={setField("contractStart")}
            />
            {errors.contractStart && <span className="am-error-msg">{errors.contractStart}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">
              Kraj ugovora
              <span className="am-field-hint">opciono</span>
            </label>
            <input
              className="am-input am-date"
              type="date"
              value={formData.contractEnd}
              onChange={setField("contractEnd")}
            />
          </div>
        </div>
      </div>

      {/* ── Sanitarna knjižica ── */}
      <div className="am-section">
        <div className="am-section-label">Sanitarna knjižica</div>
        <div className="am-form-grid">

          <div className="am-field">
            <label className="am-label">
              Sanitarna izdata
              <span className="am-field-hint">opciono</span>
            </label>
            <input
              className="am-input am-date"
              type="date"
              value={formData.sanitaryBookletIssuedAt}
              onChange={setField("sanitaryBookletIssuedAt")}
            />
          </div>

          <div className="am-field">
            <label className="am-label">
              Sanitarna važi do
              <span className="am-field-hint">opciono</span>
            </label>
            <input
              className="am-input am-date"
              type="date"
              value={formData.sanitaryBookletValidUntil}
              onChange={setField("sanitaryBookletValidUntil")}
            />
          </div>
        </div>
      </div>

      {/* ── Oznaka / boja ── */}
      <div className="am-section">
        <div className="am-section-label">Oznaka</div>
        <div className="am-field">
          <label className="am-label">
            Boja zaposlenog
            <span className="am-field-hint">za kalendar i raspored</span>
          </label>
          <div className="aum-swatches">
            {COLORS.map((c) => (
              <span
                key={c}
                className={`aum-swatch${formData.color === c ? " sel" : ""}`}
                style={{ background: c }}
                onClick={() => setFormData((prev) => ({ ...prev, color: c }))}
              />
            ))}
            <label
              className={`aum-swatch aum-swatch-custom${!COLORS.includes(formData.color) ? " sel" : ""}`}
              style={!COLORS.includes(formData.color) ? { background: formData.color } : undefined}
              title="Prilagođena boja"
            >
              <input
                type="color"
                className="aum-color-input"
                value={!COLORS.includes(formData.color) ? formData.color : "#3b82f6"}
                onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
              />
            </label>
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default AddUserModal;
